import { useQuery, useMutation } from '@tanstack/react-query';
import { reportsApi } from '../services/api';

export const useSalesSummary = (startDate, endDate) => {
  return useQuery({
    queryKey: ['salesSummary', startDate, endDate],
    queryFn: () => reportsApi.getSalesSummary(startDate, endDate),
    enabled: !!startDate && !!endDate,
  });
};

export const useTopProducts = (limit = 10) => {
  return useQuery({
    queryKey: ['topProducts', limit],
    queryFn: () => reportsApi.getTopProducts(limit),
  });
};

export const useRegionalPerformance = () => {
  return useQuery({
    queryKey: ['regionalPerformance'],
    queryFn: () => reportsApi.getRegionalPerformance(),
  });
};

export const useRevenueByCategory = () => {
  return useQuery({
    queryKey: ['revenueByCategory'],
    queryFn: () => reportsApi.getRevenueByCategory(),
  });
};

export const useKPIMetrics = () => {
  return useQuery({
    queryKey: ['kpiMetrics'],
    queryFn: () => reportsApi.getKPIMetrics(),
  });
};

export const useTrendData = (metric = 'revenue', period = 'daily') => {
  return useQuery({
    queryKey: ['trendData', metric, period],
    queryFn: () => reportsApi.getTrendData(metric, period),
  });
};

export const useCustomQuery = () => {
  return useMutation({
    mutationFn: (query) => reportsApi.executeCustomQuery(query),
  });
};

export const useTenantAdoptionReport = (startDate, endDate) => {
  return useQuery({
    queryKey: ['tenantAdoption', startDate, endDate],
    queryFn: () => reportsApi.getTenantAdoptionReport(startDate, endDate),
    enabled: !!startDate && !!endDate,
  });
};

export const useTenantAdoptionToday = () => {
  return useQuery({
    queryKey: ['tenantAdoptionToday'],
    queryFn: () => reportsApi.getTenantAdoptionToday(),
  });
};

export const useTenantAdoptionSummary = () => {
  return useQuery({
    queryKey: ['tenantAdoptionSummary'],
    queryFn: () => reportsApi.getTenantAdoptionSummary(),
  });
};

export const useCardMetricsReport = (startDate, endDate) => {
  return useQuery({
    queryKey: ['cardMetrics', startDate, endDate],
    queryFn: () => reportsApi.getCardMetricsReport(startDate, endDate),
    enabled: !!startDate && !!endDate,
  });
};

export const useCardMetricsToday = () => {
  return useQuery({
    queryKey: ['cardMetricsToday'],
    queryFn: () => reportsApi.getCardMetricsToday(),
  });
};

export const useCardMetricsSummary = () => {
  return useQuery({
    queryKey: ['cardMetricsSummary'],
    queryFn: () => reportsApi.getCardMetricsSummary(),
  });
};

export const useConsumerSearch = (searchTerm) => {
  return useQuery({
    queryKey: ['consumerSearch', searchTerm],
    queryFn: () => reportsApi.searchConsumer(searchTerm),
    enabled: !!searchTerm && searchTerm.trim().length > 0,
  });
};

export const useConsumerEnrollmentHistory = (memNbr) => {
  return useQuery({
    queryKey: ['consumerEnrollmentHistory', memNbr],
    queryFn: () => reportsApi.getConsumerEnrollmentHistory(memNbr),
    enabled: !!memNbr && memNbr.trim().length > 0,
  });
};

export const useConsumerLoginHistory = (consumerId) => {
  return useQuery({
    queryKey: ['consumerLoginHistory', consumerId],
    queryFn: () => reportsApi.getConsumerLoginHistory(consumerId),
    enabled: !!consumerId,
  });
};

export const useCardValueLoads = (consumerCode) => {
  return useQuery({
    queryKey: ['cardValueLoads', consumerCode],
    queryFn: () => reportsApi.getCardValueLoads(consumerCode),
    enabled: !!consumerCode && consumerCode.trim().length > 0,
  });
};

export const useCardProxy = (consumerCode) => {
  return useQuery({
    queryKey: ['cardProxy', consumerCode],
    queryFn: () => reportsApi.getCardProxy(consumerCode),
    enabled: !!consumerCode && consumerCode.trim().length > 0,
  });
};

export const useConsumerPerson = (consumerCode) => {
  return useQuery({
    queryKey: ['consumerPerson', consumerCode],
    queryFn: () => reportsApi.getConsumerPerson(consumerCode),
    enabled: !!consumerCode && consumerCode.trim().length > 0,
  });
};

export const useConsumerCohort = (consumerCode) => {
  return useQuery({
    queryKey: ['consumerCohort', consumerCode],
    queryFn: () => reportsApi.getConsumerCohort(consumerCode),
    enabled: !!consumerCode && consumerCode.trim().length > 0,
  });
};

export const useConsumerWallet = (consumerCode) => {
  return useQuery({
    queryKey: ['consumerWallet', consumerCode],
    queryFn: () => reportsApi.getConsumerWallet(consumerCode),
    enabled: !!consumerCode && consumerCode.trim().length > 0,
  });
};

export const useCardTransactions = (consumerCode) => {
  return useQuery({
    queryKey: ['cardTransactions', consumerCode],
    queryFn: () => reportsApi.getCardTransactions(consumerCode),
    enabled: !!consumerCode && consumerCode.trim().length > 0,
  });
};

export const useFISAddress = (cardNumberProxy) => {
  return useQuery({
    queryKey: ['fisAddress', cardNumberProxy],
    queryFn: () => reportsApi.getFISAddress(cardNumberProxy),
    enabled: !!cardNumberProxy && cardNumberProxy.trim().length > 0,
  });
};

export const useTransactionItemDetails = (txnUid, enabled = false) => {
  return useQuery({
    queryKey: ['transactionItemDetails', txnUid],
    queryFn: () => reportsApi.getTransactionItemDetails(txnUid),
    enabled: !!txnUid && txnUid.trim().length > 0 && enabled,
    staleTime: 5 * 60 * 1000,
  });
};

export const usePendingTransactions = (cardProxy) => {
  return useQuery({
    queryKey: ['pendingTransactions', cardProxy],
    queryFn: () => reportsApi.getPendingTransactions(cardProxy),
    enabled: !!cardProxy && cardProxy.trim().length > 0,
    staleTime: 5 * 60 * 1000,
  });
};

export const useDeclinedTransactions = (cardProxy) => {
  return useQuery({
    queryKey: ['declinedTransactions', cardProxy],
    queryFn: () => reportsApi.getDeclinedTransactions(cardProxy),
    enabled: !!cardProxy && cardProxy.trim().length > 0,
    staleTime: 5 * 60 * 1000,
  });
};

export const useDeclinedTransactionDetails = (txnUid, cardProxy) => {
  return useQuery({
    queryKey: ['declinedTransactionDetails', txnUid, cardProxy],
    queryFn: () => reportsApi.getDeclinedTransactionDetails(txnUid, cardProxy),
    enabled: !!txnUid && txnUid.trim().length > 0 && !!cardProxy && cardProxy.trim().length > 0,
    staleTime: 5 * 60 * 1000,
  });
};

export const useMonetaryTransactionDetails = (txnUid) => {
  return useQuery({
    queryKey: ['monetaryTransactionDetails', txnUid],
    queryFn: () => reportsApi.getMonetaryTransactionDetails(txnUid),
    enabled: !!txnUid && txnUid.trim().length > 0,
    staleTime: 5 * 60 * 1000,
  });
};

export const useTransactionSpendCategories = (txnUid) => {
  return useQuery({
    queryKey: ['transactionSpendCategories', txnUid],
    queryFn: () => reportsApi.getTransactionSpendCategories(txnUid),
    enabled: !!txnUid && txnUid.trim().length > 0,
    staleTime: 5 * 60 * 1000,
  });
};

export const useDailyCallCenterHealthSummary = () => {
  return useQuery({
    queryKey: ['dailyCallCenterHealthSummary'],
    queryFn: () => reportsApi.getDailyCallCenterHealthSummary(),
  });
};

export const useAgentProductivityDaily = () => {
  return useQuery({
    queryKey: ['agentProductivityDaily'],
    queryFn: () => reportsApi.getAgentProductivityDaily(),
  });
};

export const useConsumerRewardsHistory = (consumerCode) => {
  return useQuery({
    queryKey: ['consumerRewardsHistory', consumerCode],
    queryFn: () => reportsApi.getConsumerRewardsHistory(consumerCode),
    enabled: !!consumerCode && consumerCode.trim().length > 0,
  });
};

export const useConsumerTasks = (consumerCode) => {
  return useQuery({
    queryKey: ['consumerTasks', consumerCode],
    queryFn: () => reportsApi.getConsumerTasks(consumerCode),
    enabled: !!consumerCode && consumerCode.trim().length > 0,
  });
};

export const useCallIntentPatterns = () => {
  return useQuery({
    queryKey: ['callIntentPatterns'],
    queryFn: () => reportsApi.getCallIntentPatterns(),
  });
};

export const useCallIntentTrends = () => {
  return useQuery({
    queryKey: ['callIntentTrends'],
    queryFn: () => reportsApi.getCallIntentTrends(),
  });
};

export const useCaseStatistics = () => {
  return useQuery({
    queryKey: ['caseStatistics'],
    queryFn: () => reportsApi.getCaseStatistics(),
  });
};

export const useCaseTypeDistribution = () => {
  return useQuery({
    queryKey: ['caseTypeDistribution'],
    queryFn: () => reportsApi.getCaseTypeDistribution(),
  });
};

export const useCaseReasonDistribution = () => {
  return useQuery({
    queryKey: ['caseReasonDistribution'],
    queryFn: () => reportsApi.getCaseReasonDistribution(),
  });
};

export const useCasePatternsAI = (enabled = false) => {
  return useQuery({
    queryKey: ['casePatternsAI'],
    queryFn: () => reportsApi.analyzeCasePatterns(),
    enabled,
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
    retry: 1,
  });
};

export const useCasePatternsSql = () => {
  return useQuery({
    queryKey: ['casePatternsSql'],
    queryFn: () => reportsApi.getCasePatternsSql(),
  });
};

export const useGenerateCard35File = () => {
  return useMutation({
    mutationFn: ({ query, preview }) => reportsApi.generateCard35File(query, preview),
  });
};

export const useGenerateCard30File = () => {
  return useMutation({
    mutationFn: ({ members, preview, actionType, testProdIndicator }) => reportsApi.generateCard30File(members, preview, actionType, testProdIndicator),
  });
};

export const useEnrollmentChecklist = () => {
  return useMutation({
    mutationFn: ({ checkId }) => reportsApi.runEnrollmentCheck(checkId),
  });
};

export const useCardTransactionPatterns = () => {
  return useQuery({
    queryKey: ['cardTransactionPatterns'],
    queryFn: () => reportsApi.getCardTransactionPatterns(),
  });
};

export const useWalletTransactionDetails = () => {
  return useQuery({
    queryKey: ['walletTransactionDetails'],
    queryFn: () => reportsApi.getWalletTransactionDetails(),
  });
};

export const useReasonCodeDistribution = () => {
  return useQuery({
    queryKey: ['reasonCodeDistribution'],
    queryFn: () => reportsApi.getReasonCodeDistribution(),
  });
};

export const useMerchantsByReasonCode = (reasonCode) => {
  const reasonCodeStr = typeof reasonCode === 'string' ? reasonCode : '';
  return useQuery({
    queryKey: ['merchantsByReasonCode', reasonCodeStr],
    queryFn: () => reportsApi.getMerchantsByReasonCode(reasonCodeStr),
    enabled: !!reasonCodeStr && reasonCodeStr.trim().length > 0,
  });
};

// ============================================
// HEALTH METRICS
// ============================================

export const useHealthMetricsSummary = () => {
  return useQuery({
    queryKey: ['healthMetricsSummary'],
    queryFn: () => reportsApi.getHealthMetricsSummary(),
  });
};

export const useHealthMetricsDaily = (days = 30) => {
  return useQuery({
    queryKey: ['healthMetricsDaily', days],
    queryFn: () => reportsApi.getHealthMetricsDaily(days),
  });
};

export const useHealthMetricsByTenant = (days = 30) => {
  return useQuery({
    queryKey: ['healthMetricsByTenant', days],
    queryFn: () => reportsApi.getHealthMetricsByTenant(days),
  });
};

export const useHealthMetricTypes = () => {
  return useQuery({
    queryKey: ['healthMetricTypes'],
    queryFn: () => reportsApi.getHealthMetricTypes(),
  });
};

export const useHealthMetricsToday = () => {
  return useQuery({
    queryKey: ['healthMetricsToday'],
    queryFn: () => reportsApi.getHealthMetricsToday(),
  });
};

export const useHealthMetricsRollup = () => {
  return useQuery({
    queryKey: ['healthMetricsRollup'],
    queryFn: () => reportsApi.getHealthMetricsRollup(),
  });
};
